import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material"
import { useEffect, useState } from "react"
import { Button, Col, Container, Modal, Row } from "react-bootstrap"
import { API_ENDPOINTS } from "../../../api/endpoints"
import { useSpinner } from "../../../contexts/SpinnerProvider"
import { useApi } from "../../../hooks/useApi"
import { Action, Column } from "../../../interfaces/CamposTablaGenerica"
import { IEgresados } from "../../../interfaces/IEgresados"
import { IEventos } from "../../../interfaces/IEventos"
import { ILinkPublicoResponse } from "../../../interfaces/ILinkPublico"
import {
  IRecibo,
  IReciboEventoRequest,
  IReciboRequest,
} from "../../../interfaces/IRecibo"
import { TipoEvento } from "../../../interfaces/TipoEvento"
import { copyDataEgresado } from "../../../utils/claveAndEncryptUtils"
import { formatDateGuiones } from "../../../utils/dateUtils"
import {
  buildWhatsappUrl,
  buildQrPublicLinkMessage,
  formatPublicLinkExpiration,
  hasQrTickets,
} from "../../../utils/publicLinkUtils"
import { capitalizeNombresPropios } from "../../../utils/StringUtils"
import { getDescripcion, labelInvitado } from "../../../utils/tipoEventoTheme"
import GenericTable from "../../GenericTable/GenericTable"
import Spinner from "../../Spinner/Spinner"
import EditEgresadoModal from "../EditEgresadoModal/EditEgresadoModal"
import RecibosEventoModal from "../Recibos/RecibosEventoModal/RecibosEventoModal"
import RecibosModal from "../Recibos/RecibosModal/RecibosModal"
import TelefonoWhatsappModal from "../../TelefonoWhatsappModal/TelefonoWhatsappModal"
import QrNoTicketsModal from "../../QrNoTicketsModal/QrNoTicketsModal"
import ModalGeneradorQR from "./ModalGeneradorQR"

const BusquedaEgresados = () => {
  const [egresados, setEgresados] = useState<IEgresados[]>([])
  const [nombre, setNombre] = useState("")
  const [apellido, setApellido] = useState("")
  const [dni, setDni] = useState("")
  const [tipoEvento, setTipoEvento] = useState<TipoEvento | "">("")
  const [dniQR, setDniQR] = useState(0)
  const [claveQR, setClaveQR] = useState("")
  const [idQR, setIdQR] = useState(0)
  const [tipoEventoQR, setTipoEventoQR] = useState<string | undefined>(
    undefined,
  )
  const [categoriaQR, setCategoriaQR] = useState<string | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const API_URL = API_ENDPOINTS.EGRESADOS
  const API_URL_RECIBOS = API_ENDPOINTS.RECIBOS
  const API_URL_EVENTOS = API_ENDPOINTS.EVENTOS
  const isMobile = window.innerWidth < 768
  const { addLoading, removeLoading } = useSpinner()
  const api = useApi()
  const [idEgresado, setIdEgresado] = useState("")

  const [editModalShow, setEditModalShow] = useState(false)
  const [selectedEgresado, setSelectedEgresado] = useState<IEgresados | null>(
    null,
  )

  const [recibosModalShow, setRecibosModalShow] = useState(false)
  const [, setSelectedRecibos] = useState<IRecibo | null>(null)
  const [recibosEventoModalShow, setRecibosEventoModalShow] = useState(false)
  const [idEventoRecibos, setIdEventoRecibos] = useState("")
  const [telefonoEventoRecibos, setTelefonoEventoRecibos] = useState("")
  const [nombreClienteEventoRecibos, setNombreClienteEventoRecibos] =
    useState("")
  const [tipoEventoRecibos, setTipoEventoRecibos] = useState<
    TipoEvento | undefined
  >(undefined)

  const [deleteConfirmation, setDeleteConfirmation] = useState(false)
  const [selectedToDelete, setSelectedToDelete] = useState<IEgresados | null>(
    null,
  )
  const [copyMessage, setCopyMessage] = useState<string>("")
  const [qrNoTicketsOpen, setQrNoTicketsOpen] = useState(false)
  const [qrNoTicketsMessage, setQrNoTicketsMessage] = useState("")

  const [openQRModal, setOpenQRModal] = useState(false)
  const [egresadoWhatsappPendiente, setEgresadoWhatsappPendiente] =
    useState<IEgresados | null>(null)
  const [telefonoWhatsapp, setTelefonoWhatsapp] = useState("")
  const [guardandoTelefonoWhatsapp, setGuardandoTelefonoWhatsapp] =
    useState(false)

  const handleOpenQRModal = () => setOpenQRModal(true)
  const handleCloseQRModal = () => setOpenQRModal(false)

  const handleButtonVerQR = (egresado: IEgresados) => {
    setDniQR(egresado.dni ?? 0)
    setClaveQR(egresado.clave)
    setIdQR(egresado.id)
    setTipoEventoQR(egresado.evento?.tipo || tipoEvento || undefined)
    setCategoriaQR(egresado.categoria || undefined)
    handleOpenQRModal()
  }

  const actions: Action = {
    update: true,
    delete: true,
    copy: true,
    sendPublicQrWhatsapp: true,
    onRecibos: true,
    viewQR: true,
  }

  const mostrarDni = tipoEvento !== TipoEvento.CUMPLEANIOS_BODAS

  const columns: Column<IEgresados>[] = [
    ...(mostrarDni
      ? [{ title: "DNI", field: "dni" } as Column<IEgresados>]
      : []),
    {
      title: "Apellido",
      field: "apellido",
      render: (rowData) => (
        <span style={{ textAlign: "center", display: "block" }}>
          {capitalizeNombresPropios(rowData.apellido)}
        </span>
      ),
    },
    {
      title: "Nombre",
      field: "nombre",
      render: (rowData) => (
        <span style={{ textAlign: "center", display: "block" }}>
          {capitalizeNombresPropios(rowData.nombre)}
        </span>
      ),
    },
    {
      title: "Categoría",
      field: "categoria",
      render: (rowData: IEgresados) => (
        <span>
          {rowData.categoria
            ? rowData.categoria.charAt(0) +
              rowData.categoria.slice(1).toLowerCase()
            : "-"}
        </span>
      ),
    },
    {
      title: "Observación/Mesa",
      field: "observaciones",
      render: (rowData: IEgresados) => (
        <span style={{ whiteSpace: "nowrap" }}>
          {rowData.observaciones ?? "-"}
        </span>
      ),
    },
    {
      title: "Nombre del Evento",
      field: "evento",
      render: (rowData: IEgresados) => (
        <span style={{ whiteSpace: "nowrap" }}>
          {rowData.evento
            ? `${rowData.evento.nombre} / ${formatDateGuiones(rowData.evento.fecha)}`
            : "Sin evento"}
        </span>
      ),
    },
  ]

  const isAnyFilterSelected = () =>
    nombre.trim() !== "" || apellido.trim() !== "" || dni.trim() !== ""

  const handleSearch = async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ nombre, apellido, dni })
      if (tipoEvento) params.append("tipoEvento", tipoEvento)
      const responseData = await api.get<IEgresados[]>(
        `${API_URL}/search?${params.toString()}`,
        { showErrorToast: false },
      )
      setEgresados(responseData)
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al realizar la busqueda")
    } finally {
      setLoading(false)
    }
  }

  const callApiAndSetEgresadoPorId = async (idEgresado: number) => {
    addLoading()
    try {
      const responseData = await api.get<IEgresados>(
        `${API_URL}/${idEgresado}`,
        { showErrorToast: false },
      )
      setError("")
      setSelectedEgresado(responseData)
      setEditModalShow(true)
    } catch (e) {
      setError(
        e instanceof Error
          ? `Error al buscar egresado para editar: ${e.message}`
          : "Error desconocido",
      )
    } finally {
      removeLoading()
    }
  }

  const handleEditModalOpen = async (egresado: IEgresados) => {
    await callApiAndSetEgresadoPorId(egresado.id)
  }

  const handleEditModalClose = () => {
    setEditModalShow(false)
    setSelectedEgresado(null)
  }

  const handleRecibosModalOpen = async (item: IEgresados) => {
    setIdEgresado(item.id.toString())
    setIdEventoRecibos(item.evento?.id?.toString() ?? "")
    setTipoEventoRecibos((item.evento?.tipo ?? tipoEvento) || undefined)
    setSelectedEgresado(item)
    setRecibosModalShow(true)
  }

  const handleRecibosModalClose = () => {
    setRecibosModalShow(false)
    setSelectedRecibos(null)
    setIdEventoRecibos("")
    setTelefonoEventoRecibos("")
    setNombreClienteEventoRecibos("")
    setTipoEventoRecibos(undefined)
  }

  const showDeleteConfirmation = (item: IEgresados) => {
    setSelectedToDelete(item)
    setDeleteConfirmation(true)
  }

  const handleDeleteConfirmation = async (confirmed: boolean) => {
    if (confirmed && selectedToDelete) {
      addLoading()
      try {
        await api.del(`${API_URL}/${selectedToDelete.id}`, {
          errorMessage: "Error al eliminar el egresado",
        })
        setEgresados(
          egresados.filter((item) => item.id !== selectedToDelete.id),
        )
      } catch {
        // toast ya mostrado
      } finally {
        removeLoading()
      }
    }
    setSelectedToDelete(null)
    setDeleteConfirmation(false)
  }

  const handleEgresadoEdit = async (updatedEgresado: IEgresados) => {
    addLoading()
    try {
      const result = await api.put<IEgresados>(
        `${API_URL}/${updatedEgresado.id}`,
        updatedEgresado,
        { errorMessage: "Error al editar el egresado" },
      )
      if (result) {
        setEgresados((prev) =>
          prev.map((item) => (item.id === result.id ? result : item)),
        )
      }
    } catch {
      // toast ya mostrado
    } finally {
      removeLoading()
    }
  }

  const handleReciboAdd = async (recibo: IReciboRequest): Promise<void> => {
    addLoading()
    try {
      await api.post(API_URL_RECIBOS, recibo, {
        errorMessage: "Error al guardar el recibo",
      })
    } catch {
      // toast ya mostrado
    } finally {
      removeLoading()
    }
  }

  const handleRecibosEventoModalOpen = async (item: IEgresados) => {
    if (item.evento?.id) {
      addLoading()
      try {
        const evento = await api.get<IEventos>(
          `${API_URL_EVENTOS}/${item.evento.id}`,
          { errorMessage: "Error al obtener el evento" },
        )
        setIdEventoRecibos(evento.id.toString())
        setTipoEventoRecibos(evento.tipo)
        setTelefonoEventoRecibos(evento.telefono || "")
        setNombreClienteEventoRecibos(
          `${capitalizeNombresPropios(evento.nombreCliente || "")} ${capitalizeNombresPropios(
            evento.apellidoCliente || "",
          )}`.trim(),
        )
        setSelectedEgresado({ ...item, evento })
        setRecibosEventoModalShow(true)
      } catch {
        // toast ya mostrado
      } finally {
        removeLoading()
      }
    }
  }

  const handleReciboEventoAdd = async (
    recibo: IReciboEventoRequest,
  ): Promise<void> => {
    addLoading()
    try {
      await api.post(`${API_URL_RECIBOS}/evento`, recibo, {
        errorMessage: "Error al guardar el recibo del evento",
      })
    } catch {
      // toast ya mostrado
    } finally {
      removeLoading()
    }
  }

  useEffect(() => {
    if (nombre.trim() === "" && apellido.trim() === "" && dni.trim() === "") {
      setEgresados([])
    }
  }, [nombre, apellido, dni])

  const handleCopy = (egresado: IEgresados) => {
    copyDataEgresado(egresado, setCopyMessage).catch((error) => {
      // eslint-disable-next-line no-console
      console.error("Error al copiar:", error)
    })
  }

  const sendPublicQrWhatsapp = async (egresado: IEgresados) => {
    addLoading()
    try {
      const link = await api.post<ILinkPublicoResponse>(
        `${API_URL}/${egresado.id}/link-publico-qr`,
        undefined,
        { errorMessage: "Error al generar el link publico de QR" },
      )
      const whatsappUrl = buildWhatsappUrl(
        egresado.telefono,
        buildQrPublicLinkMessage(egresado, link.url, link.expiraEn),
      )
      if (!whatsappUrl) {
        setCopyMessage("No hay telefono valido para enviar por WhatsApp")
        setTimeout(() => setCopyMessage(""), 3000)
        return
      }
      window.open(whatsappUrl, "_blank", "noopener,noreferrer")
      setCopyMessage(
        `WhatsApp abierto, el link expira el ${formatPublicLinkExpiration(
          link.expiraEn,
        )}`,
      )
      setTimeout(() => setCopyMessage(""), 3000)
    } catch (error) {
      console.error("Error al enviar QR por WhatsApp:", error)
    } finally {
      removeLoading()
    }
  }

  const handleSendPublicQrWhatsapp = async (egresado: IEgresados) => {
    if (!hasQrTickets(egresado.tarjetasPagadas, egresado.tarjetasPagadasMenores)) {
      setQrNoTicketsMessage(
        "No se puede enviar el QR porque no tiene tarjetas asignadas.",
      )
      setQrNoTicketsOpen(true)
      return
    }
    if (!egresado.telefono?.trim()) {
      setEgresadoWhatsappPendiente(egresado)
      setTelefonoWhatsapp("")
      return
    }
    await sendPublicQrWhatsapp(egresado)
  }

  const handleGuardarTelefonoYEnviarQr = async () => {
    if (!egresadoWhatsappPendiente || !telefonoWhatsapp.trim()) return
    setGuardandoTelefonoWhatsapp(true)
    addLoading()
    try {
      const actualizado = await api.put<IEgresados>(
        `${API_URL}/${egresadoWhatsappPendiente.id}`,
        { ...egresadoWhatsappPendiente, telefono: telefonoWhatsapp.trim() },
        { errorMessage: "Error al guardar el telefono" },
      )
      const egresadoActualizado = actualizado || {
        ...egresadoWhatsappPendiente,
        telefono: telefonoWhatsapp.trim(),
      }
      setEgresados((prev) =>
        prev.map((item) =>
          item.id === egresadoActualizado.id ? egresadoActualizado : item,
        ),
      )
      setEgresadoWhatsappPendiente(null)
      setTelefonoWhatsapp("")
      await sendPublicQrWhatsapp(egresadoActualizado)
    } catch {
      // toast ya mostrado
    } finally {
      removeLoading()
      setGuardandoTelefonoWhatsapp(false)
    }
  }

  const tituloLabel = tipoEvento
    ? `Buscar ${labelInvitado(tipoEvento)}s`
    : "Buscador"

  return (
    <Container fluid style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <h1
        style={{
          fontSize: "2.25rem",
          margin: 0,
          textAlign: "center",
          marginTop: "1rem",
          marginBottom: "1.5rem",
          flexShrink: 0,
        }}
      >
        {tituloLabel}
      </h1>

      <Row className="mb-4" style={{ alignItems: "flex-end", flexShrink: 0 }}>
        <Col style={{ flex: "0 0 18%", maxWidth: "18%" }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Tipo de evento</InputLabel>
            <Select
              value={tipoEvento}
              label="Tipo de evento"
              onChange={(e) => {
                setTipoEvento(e.target.value as TipoEvento | "")
                setEgresados([])
                setDni("")
              }}
            >
              <MenuItem value="">
                <em>Todos</em>
              </MenuItem>
              {Object.values(TipoEvento).map((t) => (
                <MenuItem key={t} value={t}>
                  {getDescripcion(t)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Col>
        <Col style={{ flex: 1 }}>
          <input
            type="text"
            placeholder="Apellido"
            value={apellido}
            onChange={(e) => setApellido(e.target.value)}
            className="form-control"
          />
        </Col>
        <Col style={{ flex: 1 }}>
          <input
            type="text"
            placeholder="Nombre"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            className="form-control"
          />
        </Col>
        {mostrarDni && (
          <Col style={{ flex: 1 }}>
            <input
              type="text"
              placeholder="DNI"
              value={dni}
              onChange={(e) => setDni(e.target.value)}
              className="form-control"
            />
          </Col>
        )}
      </Row>

      <Box style={{ width: isMobile ? "80%" : "100%", flexShrink: 0 }}>
        <Button
          variant="primary"
          onClick={handleSearch}
          disabled={loading || !isAnyFilterSelected()}
        >
          Buscar
        </Button>
        {loading && <Spinner marginTop="2%" marginBottom="1%" />}
      </Box>

      {error && <div className="text-danger" style={{ flexShrink: 0 }}>{error}</div>}
      <Box style={{
        flex: "1 1 auto",
        minHeight: 0,
        overflowY: "auto",
        width: isMobile ? "80%" : "100%",
      }}>
        <GenericTable
          data={egresados}
          columns={columns}
          actions={actions}
          onUpdate={handleEditModalOpen}
          onDelete={showDeleteConfirmation}
          onCopy={handleCopy}
          onSendPublicQrWhatsapp={handleSendPublicQrWhatsapp}
          onRecibos={handleRecibosModalOpen}
          showRecibosEventoItem={(item) =>
            (item as IEgresados).evento?.tipo === TipoEvento.CUMPLEANIOS_BODAS
          }
          onRecibosEventoItem={(item) =>
            handleRecibosEventoModalOpen(item as IEgresados)
          }
          onViewQR={handleButtonVerQR}
          snackbarOpen={!!copyMessage}
          snackbarMessage={copyMessage || ""}
        />
      </Box>

      {editModalShow && (
        <EditEgresadoModal
          show={editModalShow}
          handleClose={handleEditModalClose}
          handleEgresadoEdit={handleEgresadoEdit}
          selectedEgresado={selectedEgresado}
          idEvento={selectedEgresado?.evento?.id?.toString() ?? ""}
          tipoEvento={(selectedEgresado?.evento?.tipo ?? tipoEvento) || undefined}
        />
      )}
      {recibosModalShow && (
        <RecibosModal
          show={recibosModalShow}
          handleClose={handleRecibosModalClose}
          handleReciboAdd={handleReciboAdd}
          idEgresado={idEgresado}
          idEvento={idEventoRecibos}
          tipoEvento={tipoEventoRecibos}
        />
      )}
      {recibosEventoModalShow && (
        <RecibosEventoModal
          show={recibosEventoModalShow}
          handleClose={() => setRecibosEventoModalShow(false)}
          handleReciboEventoAdd={handleReciboEventoAdd}
          idEvento={idEventoRecibos}
          tipoEvento={tipoEventoRecibos}
          nombreEvento={
            selectedEgresado?.evento
              ? `${selectedEgresado.evento.nombre} / ${formatDateGuiones(
                  selectedEgresado.evento.fecha,
                )}`
              : undefined
          }
          telefonoEvento={telefonoEventoRecibos}
          nombreClienteEvento={nombreClienteEventoRecibos}
        />
      )}
      <Modal
        show={deleteConfirmation}
        onHide={() => setDeleteConfirmation(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirmar eliminación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          ¿Estás seguro de que deseas eliminar este egresado de la lista?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => handleDeleteConfirmation(false)}
          >
            No
          </Button>
          <Button
            variant="danger"
            onClick={() => handleDeleteConfirmation(true)}
          >
            Sí
          </Button>
        </Modal.Footer>
      </Modal>
      {openQRModal && (
        <ModalGeneradorQR
          open={openQRModal}
          onClose={handleCloseQRModal}
          dni={dniQR}
          clave={claveQR}
          id={idQR}
          tipoEvento={tipoEventoQR}
          categoria={categoriaQR}
        />
      )}
      <TelefonoWhatsappModal
        show={!!egresadoWhatsappPendiente}
        nombre={
          egresadoWhatsappPendiente
            ? `${capitalizeNombresPropios(
                egresadoWhatsappPendiente.nombre || "",
              )} ${capitalizeNombresPropios(
                egresadoWhatsappPendiente.apellido || "",
              )}`.trim()
            : ""
        }
        telefono={telefonoWhatsapp}
        onTelefonoChange={setTelefonoWhatsapp}
        onCancel={() => {
          setEgresadoWhatsappPendiente(null)
          setTelefonoWhatsapp("")
        }}
        onConfirm={handleGuardarTelefonoYEnviarQr}
        isSaving={guardandoTelefonoWhatsapp}
      />
      <QrNoTicketsModal
        open={qrNoTicketsOpen}
        message={qrNoTicketsMessage}
        onClose={() => setQrNoTicketsOpen(false)}
      />
    </Container>
  )
}

export default BusquedaEgresados
